import React, { useState, useEffect } from "react";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import DataTable from "../../../components/Content/DataTable";

// Date & Time
import moment from "moment";

// Utils
import { getAlert } from "../../../../utils/SamuiUtils";
import {
  formatStringDateToDate,
  formatThaiDateUi,
  formatThaiDateUi2,
} from "../../../../utils/DateUtils";

// Components Date Filter
import DateFilterTable from "../../../components/Content/DateFilterTable";

function Main({
  masterList,
  statusColours,
  statusPaidColours,
  name,
  onPageInsert,
  onRowSelected,
  getReport,
  fetchRealtime, 
  WHID = "3",
}) {
  const [dataMasterList, setDataMasterList] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    localStorage.getItem("so_CurrentPage")
      ? Number(localStorage.getItem("so_CurrentPage"))
      : 1
  );

  const [countWaitApprove, setCountWaitApprove] = useState(0);
  const [countApprove, setCountApprove] = useState(0);
  const [countSuccess, setCountSuccess] = useState(0);
  const [countCancel, setCountCancel] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [countWaitPaid, setCountWaitPaid] = useState(0);
  const [countOverduePaid, setCountOverduePaid] = useState(0);
  const [countPaidInFull, setCountPaidInFull] = useState(0);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // ค้นหาเลขเอกสาร
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem("searchTerm") ? localStorage.getItem("searchTerm") : ""
  );
  const [filteredItems, setFilteredItems] = useState([]);
  const totalAmount = filteredItems
    .filter((item) => item.Doc_Status !== 13)
    .reduce((sum, item) => sum + (item.NetTotal || 0), 0);

  const [startDate, setStartDate] = useState(formatThaiDateUi2(new Date()));
  const [endDate, setEndDate] = useState(formatThaiDateUi2(new Date()));
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortField, setSortField] = useState("Doc_No");
  const [selectedDateType, setSelectedDateType] = useState("1"); // "1"=Doc_Date, "2"=Created_Date
  const [dateRangeType, setDateRangeType] = useState("day");

  // แปลงวันไทย -> moment
  const convertThaiDateToMoment = (thaiDateStr) => {
    const [day, month, yearBE] = thaiDateStr.split("-");
    const yearCE = parseInt(yearBE, 10) - 543;
    return moment(`${day}-${month}-${yearCE}`, "DD-MM-YYYY");
  };

  const initialize = async () => {
    try {
      if (masterList && masterList.length > 0) {
        setDataMasterList(masterList);
        setFilteredItems(masterList);

        // สรุปจำนวนสถานะ
        const statusCounts = { รอยืนยัน: 0, ยืนยัน: 0, สำเร็จ: 0, ยกเลิก: 0 };
        const statusPaidCounts = { รอจ่าย: 0, ค้างจ่าย: 0, จ่ายครบ: 0 };

        masterList.forEach((d) => {
          if (d.DocStatus_Name)
            statusCounts[d.DocStatus_Name] =
              (statusCounts[d.DocStatus_Name] || 0) + 1;
          if (d.DocStatusPaid_Name)
            statusPaidCounts[d.DocStatusPaid_Name] =
              (statusPaidCounts[d.DocStatusPaid_Name] || 0) + 1;
        });

        setCountWaitApprove(statusCounts["รอยืนยัน"]);
        setCountApprove(statusCounts["ยืนยัน"]);
        setCountSuccess(statusCounts["สำเร็จ"]);
        setCountCancel(statusCounts["ยกเลิก"]);

        setCountWaitPaid(statusPaidCounts["รอจ่าย"]);
        setCountOverduePaid(statusPaidCounts["ค้างจ่าย"]);
        setCountPaidInFull(statusPaidCounts["จ่ายครบ"]);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  // กรองด้วยเลขเอกสาร
  const filterItems = () => {
    const search = searchTerm.trim().toLowerCase();
    if (search === "") {
      setFilteredItems(dataMasterList);
      localStorage.setItem("searchTerm", "");
      return;
    }
    const filtered = dataMasterList.filter(
      (item) => item.Doc_No && item.Doc_No.toLowerCase().includes(search)
    );
    setFilteredItems(filtered);
    localStorage.setItem("searchTerm", searchTerm);
  };

  const payload = filteredItems
    ?.map((item) => item.Doc_ID)
    .filter((id) => id !== undefined && id !== null)
    .join(",");

  console.debug("payload", payload);

  // เอฟเฟกต์ flash ตารางเล็กน้อย
  const [flashStyle, setFlashStyle] = useState({});

  // ค้นหาจากช่วงวันที่ + ตัวเลือกต่าง ๆ
  const handleSearch = async () => {
    const isCreatedDate = selectedDateType === "2";
    // แปลงวันไทยเป็นช่วงเวลาเต็มวัน
    const startDefault = moment(formatStringDateToDate(startDate)).format(
      "YYYY-MM-DD 00:00:00"
    );
    const endDefault = moment(formatStringDateToDate(endDate)).format(
      "YYYY-MM-DD 23:59:59"
    );

    // ดึงข้อมูลจากเซิร์ฟเวอร์ด้วย Doc_Date (ตามสัญญาใหม่ของ fetchRealtime)
    // จากนั้นมากรองต่อฝั่งหน้าเว็บตาม isCreatedDate / includeCancelled / searchTerm
    try {
      const fetchedList = await fetchRealtime(
        startDefault,
        endDefault,
        includeCancelled
      ); // <- ไม่ส่ง SQL

      let working = Array.isArray(fetchedList) ? fetchedList : [];

      // กรองยกเลิก (ฝั่ง client)
      if (!includeCancelled) {
        working = working.filter(
          (it) =>
            it.Doc_Status === null ||
            it.Doc_Status === undefined ||
            it.Doc_Status !== 13
        );
      }

      // กรองช่วงวันตาม field ที่เลือก (Doc_Date หรือ Created_Date)
      const start = moment(startDefault, "YYYY-MM-DD HH:mm:ss");
      const end = moment(endDefault, "YYYY-MM-DD HH:mm:ss");

      working = working.filter((item) => {
        const dateStr = isCreatedDate ? item.Created_Date : item.Doc_Date;
        if (!dateStr) return false;
        const m = moment(dateStr, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss"]);
        return m.isBetween(start, end, null, "[]");
      });

      // กรองเลขเอกสารจาก searchTerm
      const lower = searchTerm.trim().toLowerCase();
      if (lower !== "") {
        working = working.filter(
          (it) => it.Doc_No && it.Doc_No.toLowerCase().includes(lower)
        );
      }
      setFilteredItems(working);

      localStorage.setItem("startDate", startDate);
      localStorage.setItem("endDate", endDate);
      localStorage.setItem("sortField", sortField);
      localStorage.setItem("sortOrder", sortOrder);
      localStorage.setItem("includeCancelled", includeCancelled.toString());
    } catch (error) {
      getAlert("FAILED", error.message);
    } finally {
      setFlashStyle({ display: "none" });
      setTimeout(() => setFlashStyle({}), 300);
    }
  };

  // แบบลัดเลือกวัน
  const DateRangeType = (type) => {
    setDateRangeType(type);
    localStorage.setItem("DateRangeType", type);
    if (type === "day") {
      setStartDate(formatThaiDateUi2(new Date()));
      setEndDate(formatThaiDateUi2(new Date()));
    } else if (type === "month") {
      setStartDate(formatThaiDateUi2(moment().startOf("month").toDate()));
      setEndDate(formatThaiDateUi2(new Date()));
    }
  };

  // ล้างค่า (optional ปล่อยไว้ใช้ภายหลังได้)
  const handleClear = () => {
    setSearchTerm("");
    setStartDate(formatThaiDateUi(moment().startOf("month").toDate()));
    setEndDate(formatThaiDateUi(new Date()));
    setSelectedDateType("1");
    setFilteredItems(dataMasterList);
    setSortField("Doc_No");
    setSortOrder("desc");
  };

  // เรียงข้อมูล
  const handleSort = () => {
    if (!sortField) return;
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);
    localStorage.setItem("sortOrder", newSortOrder);

    const sorted = [...filteredItems].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return newSortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === "string" && typeof bValue === "string") {
        return newSortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return 0;
    });

    setFilteredItems(sorted);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    localStorage.setItem("so_CurrentPage", pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  // ซ่อนสถานะบางค่าใน counter (ถ้ามีการแสดงผล)
  const filteredStatusColours = statusColours.filter(
    (s) => !["รอยืนยัน", "ปิดงาน"].includes(s.DocSetStaus_Name)
  );

  const getDailyReportParams = () => ({
    dateStart: convertThaiDateToMoment(startDate).format(
      "YYYY-MM-DD 00:00:00"
    ),
    dateEnd: convertThaiDateToMoment(endDate).format("YYYY-MM-DD 23:59:59"),
    whId: WHID,
  });

  const runDailyReport = async (reportName, reportPayload = payload) => {
    const reportParams = getDailyReportParams();

    try {
      if (
        window.electronAPI &&
        typeof window.electronAPI.runExe === "function"
      ) {
        const env = process.env.REACT_APP_REPORT_ENV || "PRD";
        const userId = localStorage.getItem("userId") || "0";
        const commandParams = `${env} Auto ${reportName} ${reportParams.whId} ${reportParams.dateStart} ${reportParams.dateEnd} ${userId}`;

        await window.electronAPI.runExe(commandParams);
        return;
      }

      await getReport(reportName, reportParams, reportPayload);
    } catch (error) {
      console.error("Electron report error:", error);
      await getReport(reportName, reportParams, reportPayload);
    }
  };

  const runReceiptReport = async (docNo) => {
    try {
      if (
        window.electronAPI &&
        typeof window.electronAPI.runExe === "function"
      ) {
        const path = `"C:\\pb_print_slip\\pb_print_slip.exe" PRD Auto SLIP ${docNo}`;
        await window.electronAPI.runExe(path);
        return;
      }

      await getReport("rc", docNo);
    } catch (error) {
      console.error("Electron receipt report error:", error);
      await getReport("rc", docNo);
    }
  };

  const handlePrint = async (type) => {
    if (type === "A4") {
      await runDailyReport("DAILY_EXP_A4");
    } else if (type === "SL") {
      await runDailyReport("DAILY_EXP_SL");
    }
    setShowPrintModal(false);
  };

  // ---------- Effects ----------
  useEffect(() => {
    initialize();
  }, [masterList]);

  useEffect(() => {
    filterItems();
  }, [searchTerm, dataMasterList]);

  useEffect(() => {
    handleSearch();
  }, [startDate, endDate, dateRangeType, includeCancelled, selectedDateType]);

  useEffect(() => {
    const savedSearch = localStorage.getItem("searchTerm");
    const savedStart = localStorage.getItem("startDate");
    const savedEnd = localStorage.getItem("endDate");
    const savedSort = localStorage.getItem("sortField");
    const savedOrder = localStorage.getItem("sortOrder");
    const savedCancel = localStorage.getItem("includeCancelled");
    const savedDateRangeType = localStorage.getItem("DateRangeType");

    if (savedSearch) setSearchTerm(savedSearch);
    if (savedStart) setStartDate(savedStart);
    if (savedEnd) setEndDate(savedEnd);
    if (savedSort) setSortField(savedSort);
    if (savedOrder) setSortOrder(savedOrder);
    if (savedCancel) setIncludeCancelled(savedCancel === "true");
    if (savedDateRangeType) setDateRangeType(savedDateRangeType);
  }, []);

  // ---------- Render ----------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center mb-0">
        <Breadcrumbs
          page={name}
          items={[
            { name: "ขายสินค้า", url: "/billofsale" },
            { name: name, url: "/billofsale" },
          ]}
        />
      </div>

      <div
        className="row hide-scrollbar"
        style={{ height: "calc(100vh - 80px)", overflowY: "auto" }}
      >
        <div className="col-12">
          <div
            className="p-3 rounded-3 my-2 shadow-lg"
            style={{ backgroundColor: "#ffff" }}
          >
            <div
              style={{
                display: "grid",
                // gap: "10px",
                gridTemplateColumns: "auto",
                // justifyContent: "space-between",
              }}
            >
              <div
                className="mb-2 align-items-center"
                style={{
                  display: "grid",
                  gridTemplateColumns: "280px 500px 200px auto",
                }}
              >
                {/* Search box */}
                <div className="" style={{ width: "11%" }}>
                  <div className="navbar-form nav-search">
                    <div className="input-group" style={{ width: "250px" }}>
                      <input
                        type="text"
                        placeholder="ค้นหาเอกสาร..."
                        className="form-control"
                        value={searchTerm}
                        onChange={(e) => {
                          handlePageChange(1);
                          setSearchTerm(e.target.value);
                        }}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleSearch}
                        type="button"
                      >
                        <i className="fas fa-search"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Date filter */}
                <div className="d-flex">
                  <DateFilterTable
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    selectedDateType={selectedDateType}
                    setSelectedDateType={setSelectedDateType}
                    handleSearch={handleSearch}
                  />
                </div>

                {/* Range & Sort */}
                <div className="d-flex col-4 col-md-2 align-items-center gap-1">
                  <div className="form-check d-flex">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="range-day"
                      value="day"
                      checked={dateRangeType === "day"}
                      onChange={(e) => DateRangeType(e.target.value)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="range-day"
                      style={{ width: "10px" }}
                    >
                      รายวัน
                    </label>
                  </div>
                  <div className="form-check d-flex">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="range-month"
                      value="month"
                      checked={dateRangeType === "month"}
                      onChange={(e) => DateRangeType(e.target.value)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="range-month"
                      style={{ width: "50px" }}
                    >
                      รายเดือน
                    </label>
                  </div>
                </div>

                <div className="d-flex" style={{ width: "300px" }}>
                  <label
                    className="form-check-label"
                    htmlFor="range-month"
                    style={{ width: "58px" }}
                  >
                    เรียงตาม
                  </label>
                  <select
                    className="form-select"
                    value={sortField}
                    onChange={(e) => {
                      setSortField(e.target.value);
                      localStorage.setItem("sortField", e.target.value);
                    }}
                    style={{ maxWidth: "150px" }}
                  >
                    <option value="Doc_No">เลขที่เอกสาร</option>
                    <option value="Doc_Date">วันที่เอกสาร</option>
                    <option value="Created_Date">วันที่สร้าง</option>
                    <option value="AR_Code">รหัสลูกค้า</option>
                    <option value="AR_Name">ชื่อลูกค้า</option>
                    <option value="NetTotal">ยอดรวม</option>
                  </select>
                  <div className="row mb-2">
                    <div className="col-1 d-flex justify-content-between">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleSort}
                        style={{
                          cursor: "pointer",
                          color: "black",
                          backgroundColor: "transparent",
                          height: "50px",
                        }}
                      >
                        <i
                          className={`fas fa-arrow-${
                            sortOrder === "asc" ? "up" : "down"
                          }`}
                        ></i>{" "}
                        Sort
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="">
                  <div
                    className=""
                    style={{
                      display: "grid",
                      gap: "10px",
                      gridTemplateColumns: "auto auto",
                      justifyContent: "space-between",
                      marginTop: "-10px",
                      // width: "90vw",
                    }}
                  >
                    {/* include cancelled */}
                    <div className=" mt-2">
                      <div
                        className="form-check"
                        style={{
                          display: "grid",
                          gap: "10px",
                          gridTemplateColumns: "auto auto",
                        }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="showCancelled"
                          checked={includeCancelled}
                          onChange={(e) =>
                            setIncludeCancelled(e.target.checked)
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="showCancelled"
                        >
                          แสดงรายการที่ยกเลิกด้วย
                        </label>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gap: "10px",
                        gridTemplateColumns: "auto auto",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gap: "10px",
                          gridTemplateColumns: "auto auto auto",
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={() => runDailyReport("DAILY_EXP_A4")}
                          className="btn text-white w-100 d-flex align-items-center justify-content-center"
                          style={{
                            whiteSpace: "nowrap",
                            minWidth: "180px",
                            fontSize: "14px",
                            height: "35px",
                            backgroundColor: "rgb(15, 108, 189)",
                          }}
                        >
                          พิมพ์รายงานแบบแจกแจง(A4)
                        </button>
                        <button
                          onClick={() => runDailyReport("DAILY_EXP_SL")}
                          className="btn text-white w-100 d-flex align-items-center justify-content-center"
                          style={{
                            whiteSpace: "nowrap",
                            minWidth: "180px",
                            fontSize: "14px",
                            height: "35px",
                            backgroundColor: "rgb(15, 108, 189)",
                          }}
                        >
                          พิมพ์รายงานแบบแจกแจง (Slip)
                        </button>
                        <button
                          onClick={() => runDailyReport("DAILY")}
                          className="btn text-white w-100 d-flex align-items-center justify-content-center"
                          style={{
                            whiteSpace: "nowrap",
                            minWidth: "180px",
                            fontSize: "14px",
                            height: "35px",
                            backgroundColor: "rgb(15, 108, 189)",
                          }}
                        >
                          พิมพ์รายงานประจำวัน
                        </button>
                      </div>

                      <div className="flex-fill d-flex">
                        <button
                          onClick={onPageInsert}
                          className="btn btn-warning text-white w-100 d-flex align-items-center justify-content-center"
                          style={{
                            whiteSpace: "nowrap",
                            minWidth: "140px",
                            fontSize: "14px",
                            minHeight: "35px",
                          }}
                        >
                          สร้าง{name}
                          <i
                            className="fa fa-plus ms-1"
                            style={{ fontSize: "14px" }}
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="table-wrapper" style={flashStyle}>
            <DataTable
              currentItems={currentItems}
              onRowSelected={onRowSelected}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              dataMasterList={filteredItems}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              setCurrentPage={setCurrentPage}
              totalAmount={totalAmount}
              fieldMappings={{
                no: "Doc_No",
                docBlNo: "Doc_BL_No",
                docInvNo: "Doc_INV_No",
                statusName: "DocStatus_Name",
                statusColour: "DocStatus_Colour",
                statusPaidName: "DocStatusPaid_Name",
                statusPaidColour: "DocStatusPaid_Colour",
                statusPaidTypeName: "DocStatusPaid_Type_Name",
                statusPaidTypeColour: "DocStatusPaidType_Colour",
                docIsPickingList: "Doc_Is_PickingList",
                date: "Doc_Date",
                apCode: "AP_Code",
                apName: "AP_Name",
                dlLineName: "DL_LineName",
                dueDate: "Created_Date",
                netTotal: "NetTotal",
                createdBy: "Created_By_Name",
                updatedBy: "Update_By_Name",
              }}
              getReport={runReceiptReport}
            />
          </div>
        </div>
        {/* Modal เลือกประเภทการพิมพ์ */}

        {showPrintModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">เลือกประเภทการพิมพ์</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowPrintModal(false)}
                  ></button>
                </div>

                <div>
                  <div
                    className="modal-body"
                    style={{
                      display: "grid",
                      padding: 10,
                      justifyItems: "anchor-center",
                    }}
                  >
                    <p className="mb-2">
                      กรุณาเลือกว่าจะพิมพ์รายงานประจำวันแบบใด
                    </p>

                    <div style={{ width: "180px", margin: 10 }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handlePrint("A4")}
                        style={{ width: "100%" }}
                      >
                        พิมพ์แบบ A4
                      </button>
                    </div>
                    <div style={{ width: "180px", margin: 10 }}>
                      {" "}
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => handlePrint("SL")}
                        style={{ width: "100%" }}
                      >
                        พิมพ์แบบสลิป (SL)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Main;
